/**
 * Default theme colors and content for the Process Steps Section block.
 */

export const THEME_COLORS = {
	primary: '#0a4f55',
	primaryGreen: '#6dbe45',
	primaryBlue: '#009bab',
	primaryLight: '#F0F6F5',
	primaryLightDark: '#D6E5E5',
	primaryTealLight: '#33817d',
	iconCircle: '#42807D',
	stepperIconCircle: '#009BAB',
	secondary: '#707070',
	bodyText: '#434A4B',
	white: '#ffffff',
};

export const LAYOUT_VARIANTS = {
	DEFAULT: 'default',
	WITH_MODAL: 'with-modal',
};

export const LAYOUT_VARIANT_OPTIONS = [
	{ label: 'Default', value: LAYOUT_VARIANTS.DEFAULT },
	{ label: 'Card with Modal', value: LAYOUT_VARIANTS.WITH_MODAL },
];

export const ARROW_COLORS = {
	DEFAULT: 'default',
	WHITE: 'white',
};

export const STEP_CIRCLE_COLORS = {
	DEFAULT: 'default',
	WHITE: 'white',
};

export const ICON_BASE = '/wp-content/themes/alteia/assets/src/images/icons/';

export const ARROW_URL =
	'/wp-content/themes/alteia/assets/src/images/process-steps-arrow.svg';

export const STEPPER_ARROW_URL =
	'/wp-content/themes/alteia/assets/src/images/down-arrow-small.svg';

export const DEFAULT_STEP_ICONS = [
	`${ ICON_BASE }murabaha-card-icon.svg`,
	`${ ICON_BASE }musharaka-card-icon.svg`,
	`${ ICON_BASE }sukuk-card-icon.svg`,
];

export const MODAL_VARIANT_STEP_ICONS = DEFAULT_STEP_ICONS;

const UNDERSIZED_THEME_ICON_FILES = [
	'commodity-icon.svg',
	'murabaha-card-icon.svg',
	'facility-type.svg',
];

export const DEFAULT_CTA_LABEL = 'Learn more';

export const DEFAULT_BADGE_COLORS = [
	'#CAB447',
	'#983920',
	'#397245',
	'#578C95',
	'#E88359',
];

export const DEFAULT_STEP = {
	iconId: 0,
	iconUrl: DEFAULT_STEP_ICONS[ 0 ],
	title: '',
	description: '',
};

export const DEFAULT_STEPS = [
	{
		iconId: 0,
		iconUrl: `${ ICON_BASE }murabaha-card-icon.svg`,
		title: 'Murabaha',
		description:
			'Operating track record, financial standing, KYC and AML screening, and the quality of trading counterparties are assessed before a facility is considered. Where appropriate, on-site due diligence and direct engagement with the borrower form part of the evaluation process.',
	},
	{
		iconId: 0,
		iconUrl: `${ ICON_BASE }musharaka-card-icon.svg`,
		title: 'Musharaka',
		description:
			'Tenor aligned to the trade cycle. Repayment source clearly identified. Security over underlying assets confirmed before approval.',
	},
	{
		iconId: 0,
		iconUrl: `${ ICON_BASE }sukuk-card-icon.svg`,
		title: 'Sukuk',
		description:
			'Financial and operational covenants are built into every facility. Defined triggers, monitoring requirements, and remedies help protect investor capital throughout the life of the transaction.',
	},
];

/**
 * Resolve a default icon URL for a step index.
 *
 * @param {number} index Step index.
 * @return {string} Icon URL.
 */
export function getDefaultStepIcon( index ) {
	return DEFAULT_STEP_ICONS[ index % DEFAULT_STEP_ICONS.length ];
}

/**
 * Resolve a default icon URL for the Card with Modal variant.
 *
 * @param {number} index Step index.
 * @return {string} Icon URL.
 */
export function getModalVariantStepIcon( index ) {
	return getDefaultStepIcon( index );
}

/**
 * Whether a stored theme icon is too small for the 144px circle.
 *
 * @param {string} iconUrl Icon URL.
 * @return {boolean} Whether the icon should be replaced.
 */
export function isUndersizedThemeIcon( iconUrl ) {
	if ( ! iconUrl ) {
		return true;
	}

	return UNDERSIZED_THEME_ICON_FILES.some( ( fileName ) =>
		iconUrl.includes( fileName )
	);
}

/**
 * Resolve a display icon, falling back to Default Variant icons when needed.
 *
 * @param {string} iconUrl Stored icon URL.
 * @param {number} [index] Step index for the fallback icon.
 * @return {string} Icon URL that fills the circle.
 */
export function resolveDisplayIconUrl( iconUrl, index = 0 ) {
	if ( ! isUndersizedThemeIcon( iconUrl ) ) {
		return iconUrl;
	}

	return getDefaultStepIcon( index );
}

/**
 * Generate a stable unique step ID.
 *
 * @param {string} [prefix] Optional slug prefix.
 * @return {string} Unique step ID.
 */
export function generateStepId( prefix = 'step' ) {
	const random = Math.random().toString( 36 ).slice( 2, 9 );
	return `${ prefix }-${ Date.now().toString( 36 ) }-${ random }`;
}

/**
 * Whether the block is using the Card with Modal layout.
 *
 * @param {string} layoutVariant Layout variant value.
 * @return {boolean} Whether the modal variant is active.
 */
export function isWithModalVariant( layoutVariant ) {
	return layoutVariant === LAYOUT_VARIANTS.WITH_MODAL;
}
